/**
 * CheckpointEditModal
 *
 * Modal dialog for editing a single checkpoint's response data.
 * Supports all checkpoint response types:
 * - Comment: Text input
 * - Status: Button selection with optional comment
 * - Time: Time picker
 * - DateTime: DateTime picker
 * - Dropdown/DropdownMultiSelect: Select input
 * - Attachment: File upload with preview
 * - Signature: Signature capture pad
 *
 * Usage:
 * <CheckpointEditModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   checkpoint={selectedCheckpoint}
 *   scheduleAssignmentId={assignmentId}
 *   onSaved={() => refetch()}
 * />
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import classNames from 'classnames';
import styles from './CheckpointEditModal.module.css';
import { TransformedCheckpoint, CheckpointDisplayStatus } from '../../hooks/dispatch/usePatrolDetail';
import { useUpdateCheckpoint, CheckpointUpdateData } from '../../hooks/dispatch/useUpdateCheckpoint';
import { CheckpointAttachment } from '../../../../../api/activityReport/getActivityReport';
import { getCheckpointUppyInstance } from '../../utils/getCheckpointUppyInstance';

// Import response type components
import CheckpointStatusResponse from '../checkpointCard/responseTypes/CheckpointStatusResponse';
import CheckpointCommentResponse from '../checkpointCard/responseTypes/CheckpointCommentResponse';
import CheckpointTimeResponse from '../checkpointCard/responseTypes/CheckpointTimeResponse';
import CheckpointDateTimeResponse from '../checkpointCard/responseTypes/CheckpointDateTimeResponse';
import CheckpointDropdownResponse from '../checkpointCard/responseTypes/CheckpointDropdownResponse';
import CheckpointAttachmentResponse from '../checkpointCard/responseTypes/CheckpointAttachmentResponse';

// ============================================
// Types
// ============================================

export interface CheckpointEditModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** The checkpoint to edit */
  checkpoint: TransformedCheckpoint | null;
  /** Schedule assignment ID for the update mutation */
  scheduleAssignmentId: number;
  /** Callback after successful save */
  onSaved?: () => void;
}

/** Local state for editing */
interface EditState {
  comment: string;
  selectedStatusId: number | null;
  time: string;
  dateTime: string | null;
  selectedDropdownIds: number[];
  attachments: PatrolAttachment[];
}

/** Attachment type matching what CheckpointAttachmentResponse expects */
interface PatrolAttachment {
  AttachmentID: number | string;
  Name: string;
  Size: number;
  ContentType: string;
}

// ============================================
// Constants
// ============================================

/** Response type labels for display */
const RESPONSE_TYPE_LABELS: Record<string, string> = {
  Comment: 'Comment',
  Status: 'Status',
  Time: 'Time',
  DateTime: 'Date & Time',
  Dropdown: 'Selection',
  DropdownMultiSelect: 'Multiple Selection',
  Attachment: 'Attachment',
  Signature: 'Signature',
};

/** Chip colors for response types */
const RESPONSE_TYPE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'info'> = {
  Comment: 'default',
  Status: 'primary',
  Time: 'info',
  DateTime: 'info',
  Dropdown: 'secondary',
  DropdownMultiSelect: 'secondary',
  Attachment: 'default',
  Signature: 'default',
};

// ============================================
// Helper Functions
// ============================================

/** Initialize edit state from checkpoint */
function initializeEditState(checkpoint: TransformedCheckpoint): EditState {
  return {
    comment: checkpoint.comment || '',
    selectedStatusId: checkpoint.selectedStatusId,
    time: checkpoint.time || '',
    dateTime: checkpoint.dateTime,
    selectedDropdownIds: checkpoint.selectedDropdownIds || [],
    attachments: (checkpoint.attachments || []).map(a => ({
      AttachmentID: a.AttachmentID,
      Name: a.Name,
      Size: a.Size,
      ContentType: a.ContentType,
    })),
  };
}

/** Parse time string to Date object */
function parseTimeToDate(timeString: string): Date | null {
  if (!timeString) return null;

  // Try to parse various time formats
  const today = new Date();
  const [hours, minutes] = timeString.split(':');
  if (hours && minutes) {
    today.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return today;
  }

  // Try parsing as date string
  const parsed = new Date(timeString);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** Format Date to time string (HH:mm) */
function formatDateToTime(date: Date | null): string {
  if (!date) return '';
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

// ============================================
// Main Component
// ============================================

const CheckpointEditModal: React.FC<CheckpointEditModalProps> = ({
  open,
  onClose,
  checkpoint,
  scheduleAssignmentId,
  onSaved,
}) => {
  // Local edit state
  const [editState, setEditState] = useState<EditState>({
    comment: '',
    selectedStatusId: null,
    time: '',
    dateTime: null,
    selectedDropdownIds: [],
    attachments: [],
  });

  // Track if comment is required based on selected status
  const [isCommentRequired, setIsCommentRequired] = useState(false);

  // Track if attachments have changed (for enabling save button)
  const [attachmentsChanged, setAttachmentsChanged] = useState(false);

  // Uppy instance ref for attachment/signature uploads
  const uppyRef = useRef<any>(null);

  // Initialize edit state and Uppy when checkpoint changes
  useEffect(() => {
    if (checkpoint && open) {
      setEditState(initializeEditState(checkpoint));
      setAttachmentsChanged(false);

      // Check if current status requires comment
      const selectedStatus = checkpoint.statuses.find(
        s => s.StatusID === checkpoint.selectedStatusId
      );
      setIsCommentRequired(selectedStatus?.isCommentRequired || false);

      // Create Uppy instance for attachment/signature types
      const responseType = checkpoint.responseTypeName?.toLowerCase();
      if (responseType === 'attachment' || responseType === 'signature') {
        uppyRef.current = getCheckpointUppyInstance(checkpoint.checkpointId);
      }
    }

    // Cleanup Uppy on close
    return () => {
      if (uppyRef.current) {
        uppyRef.current.close();
        uppyRef.current = null;
      }
    };
  }, [checkpoint, open]);

  // Update checkpoint mutation
  const { updateCheckpoint, isUpdating, error, reset } = useUpdateCheckpoint({
    scheduleAssignmentId,
    onSuccess: () => {
      onSaved?.();
      onClose();
    },
  });

  // Handle status selection
  const handleStatusChange = useCallback((statusId: number) => {
    setEditState(prev => ({ ...prev, selectedStatusId: statusId }));

    // Check if this status requires comment
    const status = checkpoint?.statuses.find(s => s.StatusID === statusId);
    setIsCommentRequired(status?.isCommentRequired || false);
  }, [checkpoint?.statuses]);

  // Handle comment change
  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditState(prev => ({ ...prev, comment: e.target.value }));
  }, []);

  // Handle time change
  const handleTimeChange = useCallback((date: Date | null) => {
    setEditState(prev => ({ ...prev, time: formatDateToTime(date) }));
  }, []);

  // Handle datetime change
  const handleDateTimeChange = useCallback((isoString: string | null) => {
    setEditState(prev => ({ ...prev, dateTime: isoString }));
  }, []);

  // Handle dropdown change
  const handleDropdownChange = useCallback((ids: number[]) => {
    setEditState(prev => ({ ...prev, selectedDropdownIds: ids }));
  }, []);

  // Handle attachment updates (from CheckpointAttachmentResponse)
  const handleAttachmentsUpdate = useCallback((attachments: PatrolAttachment[]) => {
    setEditState(prev => ({ ...prev, attachments }));
    setAttachmentsChanged(true);
    // Trigger refetch after attachment changes since they're saved directly
    onSaved?.();
  }, [onSaved]);

  // Validate before save
  const validationError = useMemo(() => {
    if (!checkpoint) return null;

    const responseType = checkpoint.responseTypeName?.toLowerCase();

    // Check if comment is required for selected status
    if (responseType === 'status' && isCommentRequired && !editState.comment.trim()) {
      return 'Comment is required for this status';
    }

    return null;
  }, [checkpoint, editState, isCommentRequired]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    if (!checkpoint) return false;

    // Attachments are saved directly via Uppy, so we track separately
    const hasTextChanges = (
      editState.comment !== (checkpoint.comment || '') ||
      editState.selectedStatusId !== checkpoint.selectedStatusId ||
      editState.time !== (checkpoint.time || '') ||
      editState.dateTime !== checkpoint.dateTime ||
      JSON.stringify(editState.selectedDropdownIds) !== JSON.stringify(checkpoint.selectedDropdownIds || [])
    );

    return hasTextChanges || attachmentsChanged;
  }, [checkpoint, editState, attachmentsChanged]);

  // Determine if this is an attachment-only type (no other fields to save)
  const isAttachmentOnlyType = useMemo(() => {
    const responseType = checkpoint?.responseTypeName?.toLowerCase();
    return responseType === 'attachment' || responseType === 'signature';
  }, [checkpoint?.responseTypeName]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!checkpoint || validationError) return;

    const updateData: CheckpointUpdateData = {
      checkpointId: checkpoint.checkpointId,
      comment: editState.comment,
      selectedStatusId: editState.selectedStatusId,
      time: editState.time,
      dateTime: editState.dateTime,
      selectedDropdownIds: editState.selectedDropdownIds,
    };

    await updateCheckpoint(updateData);
  }, [checkpoint, editState, validationError, updateCheckpoint]);

  // Handle close with reset
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // Don't render if no checkpoint
  if (!checkpoint) return null;

  const responseType = checkpoint.responseTypeName?.toLowerCase() || '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="checkpoint-edit-title"
    >
      {/* Header Bar */}
      <div
        className={classNames(styles.headerBar, styles[checkpoint.displayStatus])}
      />

      {/* Title */}
      <DialogTitle className={styles.dialogTitle} id="checkpoint-edit-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon fontSize="small" />
          <Typography className={styles.titleText}>Edit Checkpoint</Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          className={styles.closeButton}
          size="small"
          aria-label="close"
          disabled={isUpdating}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        {/* Loading Overlay */}
        {isUpdating && (
          <Box className={styles.loadingOverlay}>
            <CircularProgress />
          </Box>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" className={styles.errorAlert} onClose={reset}>
            {error.message || 'Failed to save checkpoint'}
          </Alert>
        )}

        {/* Checkpoint Info */}
        <Box className={styles.checkpointInfo}>
          <Typography className={styles.checkpointName}>
            {checkpoint.name}
          </Typography>
          {checkpoint.description && (
            <Typography className={styles.checkpointDescription}>
              {checkpoint.description}
            </Typography>
          )}
          <Chip
            label={RESPONSE_TYPE_LABELS[checkpoint.responseTypeName] || checkpoint.responseTypeName}
            size="small"
            color={RESPONSE_TYPE_COLORS[checkpoint.responseTypeName] || 'default'}
            className={styles.responseTypeChip}
          />
        </Box>

        {/* Response Input based on type */}
        <Box className={styles.inputSection}>
          {/* Status Type */}
          {responseType === 'status' && (
            <>
              <Typography className={styles.inputLabel}>Select Status</Typography>
              <CheckpointStatusResponse
                statuses={checkpoint.statuses}
                selectedStatusID={editState.selectedStatusId}
                onStatusClick={handleStatusChange}
              />

              {/* Comment field (always shown for Status type, required indicator if needed) */}
              <Box className={styles.commentSection}>
                <Typography className={styles.inputLabel}>
                  Comment {isCommentRequired && <span style={{ color: '#f44336' }}>*</span>}
                </Typography>
                <TextField
                  value={editState.comment}
                  onChange={handleCommentChange}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add a comment..."
                  error={!!validationError}
                  helperText={validationError}
                />
              </Box>
            </>
          )}

          {/* Comment Type */}
          {responseType === 'comment' && (
            <>
              <Typography className={styles.inputLabel}>Comment</Typography>
              <TextField
                value={editState.comment}
                onChange={handleCommentChange}
                fullWidth
                multiline
                rows={4}
                placeholder="Enter your response..."
              />
            </>
          )}

          {/* Time Type */}
          {responseType === 'time' && (
            <>
              <Typography className={styles.inputLabel}>Time</Typography>
              <CheckpointTimeResponse
                startTime={parseTimeToDate(editState.time)}
                onUpdateTime={handleTimeChange}
              />
            </>
          )}

          {/* DateTime Type */}
          {responseType === 'datetime' && (
            <>
              <Typography className={styles.inputLabel}>Date & Time</Typography>
              <CheckpointDateTimeResponse
                value={editState.dateTime}
                onChange={handleDateTimeChange}
              />
            </>
          )}

          {/* Dropdown Type */}
          {(responseType === 'dropdown' || responseType === 'dropdownmultiselect') && (
            <>
              <Typography className={styles.inputLabel}>
                {responseType === 'dropdownmultiselect' ? 'Select Options' : 'Select Option'}
              </Typography>
              <CheckpointDropdownResponse
                dropdownItems={checkpoint.dropdowns}
                selectedIDs={editState.selectedDropdownIds}
                allowMultiSelect={responseType === 'dropdownmultiselect'}
                setSelectedDropdownIDs={handleDropdownChange}
              />
            </>
          )}

          {/* Attachment Type */}
          {responseType === 'attachment' && uppyRef.current && (
            <>
              <Typography className={styles.inputLabel}>Attachments</Typography>
              <CheckpointAttachmentResponse
                uppy={uppyRef.current}
                checkpointID={checkpoint.checkpointId}
                attachments={editState.attachments}
                onUpdateAttachments={handleAttachmentsUpdate}
                isTypeSignature={false}
              />
            </>
          )}

          {/* Signature Type */}
          {responseType === 'signature' && uppyRef.current && (
            <>
              <Typography className={styles.inputLabel}>Signature</Typography>
              <CheckpointAttachmentResponse
                uppy={uppyRef.current}
                checkpointID={checkpoint.checkpointId}
                attachments={editState.attachments}
                onUpdateAttachments={handleAttachmentsUpdate}
                isTypeSignature={true}
              />
            </>
          )}
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions className={styles.dialogActions}>
        <Button variant="outlined" onClick={handleClose} disabled={isUpdating}>
          {isAttachmentOnlyType ? 'Close' : 'Cancel'}
        </Button>
        {!isAttachmentOnlyType && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isUpdating || !!validationError || !hasChanges}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CheckpointEditModal;
